import React, { useMemo, useState } from 'react';
import { X, Calculator, Plus, Minus } from 'lucide-react';

/**
 * Calculateur de métré — surface, périmètre, volume, linéaire, toiture
 * Bilingue FR/EN, supporte impérial (pi/po) ET métrique (m/cm).
 * Calcule pertes/coupes et soustraction d'ouvertures.
 *
 * Props :
 *  - open: boolean
 *  - onClose()
 *  - onApply({ quantity: number, unit: string, targetIdx: number | null })
 *      Si invoqué depuis la ligne X → targetIdx déjà connu côté parent
 *      Si invoqué depuis la tuile → targetIdx vient du dropdown interne
 *  - lang: 'fr' | 'en'
 *  - defaultMode: 'surface' | 'perimeter' | 'volume' | 'linear' | 'roof'
 *  - materials?: Array<{ id, description, quantity, unit }>  // si fourni, on affiche un dropdown pour choisir où appliquer
 *  - prefilledTargetIdx?: number | null  // si défini, on cache le dropdown
 */
const TXT = {
  fr: {
    title: 'Calculateur de métré',
    subtitle: 'Calcule rapidement la quantité à commander',
    mode: 'Type de calcul',
    modes: {
      surface: 'Surface (plancher, mur, gypse, peinture, céramique)',
      perimeter: 'Périmètre (plinthe, moulure, baseboard)',
      volume: 'Volume (béton, terre, gravier)',
      linear: 'Linéaire (tuyau, fil, gouttière)',
      roof: 'Toiture (avec pente)',
    },
    units: 'Système d\u2019unités',
    imperial: 'Impérial (pi, po)',
    metric: 'Métrique (m, cm)',
    length: 'Longueur',
    width: 'Largeur',
    height: 'Hauteur',
    pitch: 'Pente (rise/run)',
    pitchHelp: 'Ex: 6 pour une pente 6/12 (15°)',
    waste: 'Perte / coupe',
    wasteHelp: '10% pour bois, 15% pour céramique en diagonale',
    openings: 'Ouvertures à soustraire (portes, fenêtres)',
    addOpening: 'Ajouter une ouverture',
    openingName: 'Nom',
    result: 'Résultat',
    rawTotal: 'Surface brute',
    netTotal: 'Net (− ouvertures)',
    withWaste: 'Avec perte',
    final: 'À COMMANDER',
    apply: 'Appliquer à la quantité',
    applyTo: 'Appliquer à',
    applyToNone: '— Ne rien appliquer, juste afficher —',
    addAsNewMat: '+ Ajouter comme nouveau matériau',
    cancel: 'Annuler',
    sqUnit: { imperial: 'pi²', metric: 'm²' },
    cuUnit: { imperial: 'pi³', metric: 'm³' },
    lnUnit: { imperial: 'pi', metric: 'm' },
  },
  en: {
    title: 'Quantity calculator',
    subtitle: 'Quickly figure out how much material to order',
    mode: 'Calculation type',
    modes: {
      surface: 'Area (floor, wall, drywall, paint, tile)',
      perimeter: 'Perimeter (baseboard, trim)',
      volume: 'Volume (concrete, soil, gravel)',
      linear: 'Linear (pipe, wire, gutter)',
      roof: 'Roof (with pitch)',
    },
    units: 'Unit system',
    imperial: 'Imperial (ft, in)',
    metric: 'Metric (m, cm)',
    length: 'Length',
    width: 'Width',
    height: 'Height',
    pitch: 'Pitch (rise/run)',
    pitchHelp: 'Ex: 6 for a 6/12 pitch (15°)',
    waste: 'Waste / cuts',
    wasteHelp: '10% for wood, 15% for diagonal tile',
    openings: 'Openings to subtract (doors, windows)',
    addOpening: 'Add opening',
    openingName: 'Name',
    result: 'Result',
    rawTotal: 'Gross area',
    netTotal: 'Net (− openings)',
    withWaste: 'With waste',
    final: 'TO ORDER',
    apply: 'Apply to quantity',
    applyTo: 'Apply to',
    applyToNone: '— Just show me, don\u2019t apply —',
    addAsNewMat: '+ Add as a new material',
    cancel: 'Cancel',
    sqUnit: { imperial: 'sq ft', metric: 'm²' },
    cuUnit: { imperial: 'cu yd', metric: 'm³' },
    lnUnit: { imperial: 'ft', metric: 'm' },
  },
};

const MODES = ['surface', 'perimeter', 'volume', 'linear', 'roof'];

// === Helpers ===
// Imperial volume traditionally uses cubic yards for concrete. 1 yd³ = 27 ft³.
const cuYards = (cuFeet) => cuFeet / 27;

function pitchAngleRad(rise, run = 12) {
  if (!rise || rise <= 0) return 0;
  return Math.atan(rise / run);
}

export default function MetreCalculator({ open, onClose, onApply, lang = 'fr', defaultMode = 'surface', materials = null, prefilledTargetIdx = null }) {
  const T = TXT[lang] || TXT.fr;
  const [mode, setMode] = useState(defaultMode);
  const [units, setUnits] = useState('imperial');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [pitch, setPitch] = useState('6');
  const [waste, setWaste] = useState('10');
  const [openings, setOpenings] = useState([]); // [{ name, l, w }]
  // -1 = none, -2 = add as new material, >=0 = index into materials
  // Default on first render — parent forces remount (via key) when needed.
  const [targetIdx, setTargetIdx] = useState(
    prefilledTargetIdx !== null && prefilledTargetIdx !== undefined ? prefilledTargetIdx : -2,
  );

  const L = Number(length) || 0;
  const W = Number(width) || 0;
  const H = Number(height) || 0;

  // Compute result in chosen units
  const calc = useMemo(() => {
    const wastePct = Math.max(0, Math.min(100, Number(waste) || 0)) / 100;
    let raw = 0;
    let unit = '';
    let isVolume = false;

    if (mode === 'surface') {
      raw = L * W;
      unit = T.sqUnit[units];
    } else if (mode === 'perimeter') {
      raw = 2 * (L + W);
      unit = T.lnUnit[units];
    } else if (mode === 'volume') {
      raw = L * W * H;
      isVolume = true;
      // Imperial: convert ft³ → yd³ (standard for concrete in Canada)
      if (units === 'imperial') raw = cuYards(raw);
      unit = T.cuUnit[units];
    } else if (mode === 'linear') {
      raw = L;
      unit = T.lnUnit[units];
    } else if (mode === 'roof') {
      // Roof surface = (L × W) / cos(angle)
      const angle = pitchAngleRad(Number(pitch) || 0, 12);
      const factor = angle > 0 ? 1 / Math.cos(angle) : 1;
      raw = L * W * factor;
      unit = T.sqUnit[units];
    }

    // Subtract openings (only relevant for surface)
    let openingsTotal = 0;
    if (mode === 'surface' || mode === 'roof') {
      openingsTotal = openings.reduce((acc, o) => acc + (Number(o.l) || 0) * (Number(o.w) || 0), 0);
    }
    const net = Math.max(0, raw - openingsTotal);
    const withWaste = net * (1 + wastePct);

    return {
      raw: Math.round(raw * 100) / 100,
      openingsTotal: Math.round(openingsTotal * 100) / 100,
      net: Math.round(net * 100) / 100,
      withWaste: Math.round(withWaste * 100) / 100,
      unit,
      isVolume,
    };
  }, [mode, units, L, W, H, pitch, waste, openings, T]);

  if (!open) return null;

  const showW = mode !== 'linear';
  const showH = mode === 'volume';
  const showPitch = mode === 'roof';
  const showOpenings = mode === 'surface' || mode === 'roof';

  const inputLabel = units === 'imperial' ? 'pi' : 'm';

  const apply = () => {
    onApply({ quantity: calc.withWaste, unit: calc.unit, targetIdx });
    onClose();
  };

  const showTargetPicker = materials !== null && prefilledTargetIdx === null;

  return (
    <div
      data-testid="metre-calc-modal"
      className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl flex flex-col overflow-hidden max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-[#E5DCC9] flex items-center justify-between bg-[#FAF7F2]">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[#7A1F2B] text-[#C9A961] flex items-center justify-center">
              <Calculator size={18} />
            </div>
            <div>
              <div className="font-heading font-black text-lg leading-tight">{T.title}</div>
              <div className="text-[11px] text-gray-500">{T.subtitle}</div>
            </div>
          </div>
          <button data-testid="metre-calc-close" onClick={onClose} className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-gray-100">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-4 space-y-4">
          {/* Mode */}
          <div>
            <label className="label">{T.mode}</label>
            <select
              data-testid="metre-mode"
              className="field"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              {MODES.map((m) => (
                <option key={m} value={m}>{T.modes[m]}</option>
              ))}
            </select>
          </div>

          {/* Units toggle */}
          <div>
            <label className="label">{T.units}</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="metre-unit-imperial"
                onClick={() => setUnits('imperial')}
                className={`h-11 rounded-lg border-2 text-xs font-bold uppercase tracking-wider ${units === 'imperial' ? 'border-[#7A1F2B] bg-[#7A1F2B] text-white' : 'border-gray-200 text-gray-600'}`}
              >
                {T.imperial}
              </button>
              <button
                type="button"
                data-testid="metre-unit-metric"
                onClick={() => setUnits('metric')}
                className={`h-11 rounded-lg border-2 text-xs font-bold uppercase tracking-wider ${units === 'metric' ? 'border-[#7A1F2B] bg-[#7A1F2B] text-white' : 'border-gray-200 text-gray-600'}`}
              >
                {T.metric}
              </button>
            </div>
          </div>

          {/* Dimensions */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">{T.length} ({inputLabel})</label>
              <input
                data-testid="metre-length"
                type="number"
                step="0.01"
                className="field"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                placeholder="0"
              />
            </div>
            {showW && (
              <div>
                <label className="label">{T.width} ({inputLabel})</label>
                <input
                  data-testid="metre-width"
                  type="number"
                  step="0.01"
                  className="field"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  placeholder="0"
                />
              </div>
            )}
            {showH && (
              <div>
                <label className="label">{T.height} ({inputLabel})</label>
                <input
                  data-testid="metre-height"
                  type="number"
                  step="0.01"
                  className="field"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder="0"
                />
              </div>
            )}
            {showPitch && (
              <div>
                <label className="label">{T.pitch}</label>
                <input
                  data-testid="metre-pitch"
                  type="number"
                  step="1"
                  className="field"
                  value={pitch}
                  onChange={(e) => setPitch(e.target.value)}
                  placeholder="6"
                />
                <div className="text-[10px] text-gray-500 mt-1">{T.pitchHelp}</div>
              </div>
            )}
          </div>

          {/* Waste */}
          <div>
            <label className="label">{T.waste} (%)</label>
            <input
              data-testid="metre-waste"
              type="number"
              step="1"
              min="0"
              max="100"
              className="field"
              value={waste}
              onChange={(e) => setWaste(e.target.value)}
              placeholder="10"
            />
            <div className="text-[10px] text-gray-500 mt-1">{T.wasteHelp}</div>
          </div>

          {/* Openings */}
          {showOpenings && (
            <div className="border-t border-gray-200 pt-3">
              <div className="flex items-center justify-between mb-2">
                <label className="label !mb-0">{T.openings}</label>
                <button
                  type="button"
                  data-testid="metre-add-opening"
                  onClick={() => setOpenings([...openings, { name: '', l: '', w: '' }])}
                  className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border-2 border-dashed border-gray-300 text-xs font-bold text-gray-600 hover:border-[#7A1F2B] hover:text-[#7A1F2B]"
                >
                  <Plus size={14} /> {T.addOpening}
                </button>
              </div>
              {openings.map((o, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 mb-2">
                  <input
                    className="field col-span-5"
                    placeholder={T.openingName}
                    value={o.name}
                    onChange={(e) => {
                      const arr = [...openings];
                      arr[idx] = { ...arr[idx], name: e.target.value };
                      setOpenings(arr);
                    }}
                  />
                  <input
                    type="number" step="0.01" className="field col-span-3"
                    placeholder={T.length}
                    value={o.l}
                    onChange={(e) => {
                      const arr = [...openings];
                      arr[idx] = { ...arr[idx], l: e.target.value };
                      setOpenings(arr);
                    }}
                  />
                  <input
                    type="number" step="0.01" className="field col-span-3"
                    placeholder={T.width}
                    value={o.w}
                    onChange={(e) => {
                      const arr = [...openings];
                      arr[idx] = { ...arr[idx], w: e.target.value };
                      setOpenings(arr);
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setOpenings(openings.filter((_, i) => i !== idx))}
                    className="col-span-1 h-11 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg"
                    aria-label="remove opening"
                  >
                    <Minus size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Apply target picker (only shown when invoked from the standalone tile) */}
          {showTargetPicker && (
            <div className="border-t border-gray-200 pt-3">
              <label className="label">{T.applyTo}</label>
              <select
                data-testid="metre-target-select"
                className="field"
                value={targetIdx}
                onChange={(e) => setTargetIdx(Number(e.target.value))}
              >
                <option value={-2}>{T.addAsNewMat}</option>
                {(materials || []).map((m, idx) => (
                  <option key={m.id || idx} value={idx}>
                    {`#${idx + 1} — ${m.description || (lang === 'fr' ? 'Matériau sans nom' : 'Unnamed material')}`}
                    {m.quantity ? ` (${m.quantity} ${m.unit || ''})` : ''}
                  </option>
                ))}
                <option value={-1}>{T.applyToNone}</option>
              </select>
            </div>
          )}

          {/* Result */}
          <div className="bg-[#FAF7F2] border-l-4 border-[#C9A961] rounded-r-lg p-4 space-y-1 text-sm">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#7A1F2B] mb-2">{T.result}</div>
            <div className="flex justify-between text-gray-600">
              <span>{T.rawTotal}</span>
              <span data-testid="metre-raw">{calc.raw.toLocaleString()} {calc.unit}</span>
            </div>
            {calc.openingsTotal > 0 && (
              <>
                <div className="flex justify-between text-gray-600">
                  <span>− ouvertures</span>
                  <span>{calc.openingsTotal.toLocaleString()} {calc.unit}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>{T.netTotal}</span>
                  <span>{calc.net.toLocaleString()} {calc.unit}</span>
                </div>
              </>
            )}
            <div className="flex justify-between text-gray-600">
              <span>+ {T.waste} ({waste}%)</span>
              <span>{calc.withWaste.toLocaleString()} {calc.unit}</span>
            </div>
            <div className="h-px bg-[#E5DCC9] my-2" />
            <div className="flex justify-between items-baseline">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#7A1F2B]">{T.final}</span>
              <span data-testid="metre-final" className="font-heading font-black text-2xl text-[#7A1F2B]">
                {calc.withWaste.toLocaleString()} <span className="text-base">{calc.unit}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#E5DCC9] p-3 grid grid-cols-2 gap-2 bg-white">
          <button
            data-testid="metre-cancel"
            onClick={onClose}
            className="btn-outline"
          >
            {T.cancel}
          </button>
          <button
            data-testid="metre-apply"
            onClick={apply}
            disabled={calc.withWaste <= 0}
            className="btn-primary disabled:opacity-50"
          >
            {targetIdx === -1
              ? (lang === 'fr' ? 'Fermer' : 'Close')
              : targetIdx === -2
              ? (lang === 'fr' ? 'Ajouter le matériau' : 'Add material')
              : T.apply}
          </button>
        </div>
      </div>
    </div>
  );
}
